import type { StreamingBlock, StreamingOperation } from "./streamingTypes";

export type ActivityCategory =
  | "answer"
  | "reasoning"
  | "web_search"
  | "kb_search"
  | "chart"
  | "planning"
  | "report"
  | "ask_user"
  | "presentation"
  | "image"
  | "video"
  | "task"
  | "scout"
  | "skills"
  | "crafter"
  | "collaborate"
  | "task_board"
  | "memory"
  | "notion"
  | "linear"
  | "github"
  | "gdrive"
  | "slack"
  | "gmail"
  | "calendar"
  | "contacts"
  | "mcp"
  | "integrations"
  | "chats"
  | "live_sessions"
  | "user_interjection"
  | "reply"
  | "system"
  | "unknown";

interface ActivityConfig {
  activities: string[];

  category: ActivityCategory;
  iconKey: string;
  titleKey: string;
  titleKeyComplete?: string;
  colorClass: string;
  textColorClass: string;
  categoryLabelKey?: string;

  canBeEmpty?: boolean;
  emptyCheck?: (blocks: StreamingBlock[]) => boolean;
  emptyMessageKey?: string;

  shouldAutoCollapse?: boolean;
  isSystemOperation?: boolean;
  isProgressOperation?: boolean;
  isTimelineTool?: boolean;

  isSearchLike?: boolean;
  isContentGeneration?: boolean;
}

// About the activity naming convention adopted here:
// - Activity strings should use snake_case (e.g., "scout", "chart_generation")
// - Human-readable variants exist only for
//   backwards compatibility with older chats.
//   Do not use them for new activities
// - New activities should only have the snake_case variant

interface IntegrationStyle {
  category: ActivityCategory;
  iconKey: string;
  colorClass: string;
  textColorClass: string;
  categoryLabelKey: string;
}

// Integrations expose a family of tools that share an icon, color, and category
// label; only the verb changes per tool.
const integrationTool =
  (style: IntegrationStyle) =>
  (activities: string[], titleKey: string, titleKeyComplete: string): ActivityConfig => ({
    activities,
    titleKey,
    titleKeyComplete,
    shouldAutoCollapse: true,
    ...style,
  });

const notionTool = integrationTool({
  category: "notion",
  iconKey: "brand-notion",
  colorClass: "bg-neutral-800 dark:bg-neutral-700",
  textColorClass: "text-neutral-700 dark:text-neutral-300",
  categoryLabelKey: "activity_category_notion",
});

const linearTool = integrationTool({
  category: "linear",
  iconKey: "brand-linear",
  colorClass: "bg-indigo-600 dark:bg-indigo-500",
  textColorClass: "text-indigo-600 dark:text-indigo-400",
  categoryLabelKey: "activity_category_linear",
});

const githubTool = integrationTool({
  category: "github",
  iconKey: "brand-github",
  colorClass: "bg-neutral-900 dark:bg-neutral-700",
  textColorClass: "text-neutral-800 dark:text-neutral-300",
  categoryLabelKey: "activity_category_github",
});

const gdriveTool = integrationTool({
  category: "gdrive",
  iconKey: "brand-google-drive",
  colorClass: "bg-green-600 dark:bg-green-500",
  textColorClass: "text-green-600 dark:text-green-400",
  categoryLabelKey: "activity_category_gdrive",
});

const slackTool = integrationTool({
  category: "slack",
  iconKey: "brand-slack",
  colorClass: "bg-purple-700 dark:bg-purple-600",
  textColorClass: "text-purple-700 dark:text-purple-400",
  categoryLabelKey: "activity_category_slack",
});

const gmailTool = integrationTool({
  category: "gmail",
  iconKey: "brand-gmail",
  colorClass: "bg-rose-600 dark:bg-rose-500",
  textColorClass: "text-rose-600 dark:text-rose-400",
  categoryLabelKey: "activity_category_gmail",
});

const calendarTool = integrationTool({
  category: "calendar",
  iconKey: "calendar",
  colorClass: "bg-sky-600 dark:bg-sky-500",
  textColorClass: "text-sky-600 dark:text-sky-400",
  categoryLabelKey: "activity_category_calendar",
});

const contactsTool = integrationTool({
  category: "contacts",
  iconKey: "address-book",
  colorClass: "bg-amber-600 dark:bg-amber-500",
  textColorClass: "text-amber-600 dark:text-amber-400",
  categoryLabelKey: "activity_category_contacts",
});

const mcpTool = integrationTool({
  category: "mcp",
  iconKey: "plug",
  colorClass: "bg-teal-600 dark:bg-teal-500",
  textColorClass: "text-teal-600 dark:text-teal-400",
  categoryLabelKey: "activity_category_mcp",
});

function jsonPayload(block: StreamingBlock): Record<string, unknown> | undefined {
  if (block.finalContent?.type !== "text") return undefined;
  try {
    const data: unknown = JSON.parse(block.finalContent.text);
    return data && typeof data === "object" ? (data as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

const ACTIVITY_REGISTRY: Record<string, ActivityConfig> = {
  answer: {
    activities: ["answer", "stream_response_v1", "direct_response_v1"],
    category: "answer",
    iconKey: "check",
    titleKey: "chat_answer",
    colorClass: "bg-dark-icon",
    textColorClass: "text-gray-600 dark:text-gray-400",
    shouldAutoCollapse: false,
  },
  reasoning: {
    activities: ["reasoning"],
    category: "reasoning",
    iconKey: "brain",
    titleKey: "activity.reasoning_process",
    colorClass: "bg-purple-600 dark:bg-purple-500",
    textColorClass: "text-purple-600 dark:text-purple-400",
    categoryLabelKey: "activity.reasoning",
    shouldAutoCollapse: true,
  },
  planning: {
    activities: ["planning_v1"],
    category: "planning",
    iconKey: "route",
    titleKey: "chat_thinking",
    colorClass: "bg-indigo-500 dark:bg-indigo-400",
    textColorClass: "text-indigo-500 dark:text-indigo-400",
    categoryLabelKey: "activity.planning",
    shouldAutoCollapse: true,
  },
  kb_search: {
    activities: ["kb_search", "user_kb_search_v1", "knowledge_base_search"],
    category: "kb_search",
    iconKey: "books",
    titleKey: "activity_searching_kb",
    titleKeyComplete: "activity_searched_kb",
    colorClass: "bg-stone-500 dark:bg-stone-600",
    textColorClass: "text-stone-500 dark:text-stone-600",
    categoryLabelKey: "activity_category_kb_search",
    shouldAutoCollapse: true,
    canBeEmpty: true,
    isTimelineTool: true,
    isSearchLike: true,
    emptyCheck: (blocks) => {
      const searchBlock = blocks.find((b) => b.searchResults);
      const hasSearchAssets = (() => {
        if (!searchBlock?.searchResults) return false;
        const assets =
          searchBlock.searchResults.results?.value?.assets ||
          (searchBlock.searchResults as { assets?: { assets?: unknown[] } }).assets?.assets ||
          [];
        return assets.length > 0;
      })();

      const structuredBlocks = blocks.filter((b) => b.structuredResults && !b.structuredResults.isCompletion);
      const hasStructuredData = structuredBlocks.some((b) => {
        const sr = b.structuredResults;
        if (!sr) return false;
        const rowCount = sr.rowCount || sr.rows?.length || 0;
        return rowCount > 0;
      });

      return !hasSearchAssets && !hasStructuredData;
    },
    emptyMessageKey: "empty.no_results",
  },
  web_search: {
    activities: ["web_search", "web_search_v1"],
    category: "web_search",
    iconKey: "globe",
    titleKey: "activity_searching_web",
    titleKeyComplete: "activity_searched_web",
    colorClass: "bg-green-600 dark:bg-green-500",
    textColorClass: "text-teal-600 dark:text-teal-400",
    categoryLabelKey: "activity_category_web_search",
    shouldAutoCollapse: true,
    canBeEmpty: true,
    isTimelineTool: true,
    isSearchLike: true,
    emptyCheck: (blocks) => {
      const searchBlock = blocks.find((b) => b.webSearchResults);
      if (!searchBlock?.webSearchResults) return true;
      return (searchBlock.webSearchResults.results?.length ?? 0) === 0;
    },
    emptyMessageKey: "empty.no_results",
  },
  web_page: {
    activities: ["web_page"],
    category: "web_search",
    iconKey: "globe",
    titleKey: "activity_fetching_web",
    titleKeyComplete: "activity_fetched_web",
    colorClass: "bg-green-600 dark:bg-green-500",
    textColorClass: "text-teal-600 dark:text-teal-400",
    categoryLabelKey: "activity_category_web_search",
    shouldAutoCollapse: true,
    canBeEmpty: true,
    isTimelineTool: true,
    isSearchLike: true,
    emptyCheck: (blocks) => {
      const hasWebResults = blocks.some((b) => !!b.webSearchResults);
      return !hasWebResults;
    },
    emptyMessageKey: "empty.no_results",
  },
  web_fetch: {
    activities: ["web_fetch"],
    category: "web_search",
    iconKey: "globe",
    titleKey: "activity_fetching_web",
    titleKeyComplete: "activity_fetched_web",
    colorClass: "bg-green-600 dark:bg-green-500",
    textColorClass: "text-teal-600 dark:text-teal-400",
    canBeEmpty: true,
    emptyCheck: (blocks) => blocks.length === 0,
    emptyMessageKey: "web_fetch_no_results",
    isSearchLike: true,
  },
  structured_search: {
    activities: ["structured_search_v1"],
    category: "kb_search",
    iconKey: "database",
    titleKey: "activity_querying_data",
    titleKeyComplete: "activity_queried_data",
    colorClass: "bg-stone-500 dark:bg-stone-600",
    textColorClass: "text-stone-500 dark:text-stone-600",
    shouldAutoCollapse: true,
    canBeEmpty: true,
    isSearchLike: true,
  },
  image_generation: {
    // dall-e, banana and gemini-imagen are retired tools, kept so older chats still render.
    activities: ["image_generation", "image-gen", "dall-e", "banana", "gemini-imagen"],
    category: "image",
    iconKey: "photo",
    titleKey: "activity_generating_image",
    titleKeyComplete: "activity_image_generated",
    colorClass: "bg-orange-600 dark:bg-orange-500",
    textColorClass: "text-orange-600 dark:text-orange-400",
    isTimelineTool: true,
    isContentGeneration: true,
  },
  video_generation: {
    activities: ["video_generation", "video-gen"],
    category: "video",
    iconKey: "video",
    titleKey: "activity_generating_video",
    titleKeyComplete: "activity_video_generated",
    colorClass: "bg-rose-600 dark:bg-rose-500",
    textColorClass: "text-rose-600 dark:text-rose-400",
    categoryLabelKey: "entity.video",
    isTimelineTool: true,
    isContentGeneration: true,
  },
  chart_generation: {
    activities: ["chart_generation", "chart"],
    category: "chart",
    iconKey: "chart",
    titleKey: "activity_generating_chart",
    titleKeyComplete: "activity_chart_generated",
    colorClass: "bg-blue-600 dark:bg-blue-500",
    textColorClass: "text-blue-600 dark:text-blue-400",
    categoryLabelKey: "entity.chart",
    isContentGeneration: true,
  },
  presentation_generation: {
    activities: ["presentation_generation", "presentation"],
    category: "presentation",
    iconKey: "presentation",
    titleKey: "common.generating_presentation",
    titleKeyComplete: "entity.presentation",
    colorClass: "bg-orange-600 dark:bg-orange-500",
    textColorClass: "text-orange-600 dark:text-orange-400",
    categoryLabelKey: "entity.presentation",
    isTimelineTool: true,
    isContentGeneration: true,
  },
  report: {
    activities: ["report", "report_generation"],
    category: "report",
    iconKey: "file-text",
    titleKey: "activity_generating_report",
    titleKeyComplete: "activity_report_generated",
    colorClass: "bg-violet-600 dark:bg-violet-500",
    textColorClass: "text-violet-600 dark:text-violet-400",
    categoryLabelKey: "activity_category_report",
    isContentGeneration: true,
  },
  ask_user_question: {
    activities: ["ask_user_question"],
    category: "ask_user",
    iconKey: "message-question",
    titleKey: "activity_ask_user",
    colorClass: "bg-teal-600 dark:bg-teal-500",
    textColorClass: "text-teal-600 dark:text-teal-400",
    categoryLabelKey: "activity_category_ask_user",
  },
  read_concepts: {
    activities: ["read_concepts"],
    category: "kb_search",
    iconKey: "bulb",
    titleKey: "activity_reading_concepts",
    titleKeyComplete: "activity_read_concepts",
    colorClass: "bg-amber-600 dark:bg-amber-500",
    textColorClass: "text-amber-600 dark:text-amber-400",
  },
  scout: {
    activities: ["scout"],
    category: "scout",
    iconKey: "address-book",
    titleKey: "activity_scout_search",
    titleKeyComplete: "entity.scout",
    colorClass: "bg-indigo-600 dark:bg-indigo-500",
    textColorClass: "text-indigo-600 dark:text-indigo-400",
    categoryLabelKey: "entity.scout",
    shouldAutoCollapse: true,
    canBeEmpty: true,
    isTimelineTool: true,
    isSearchLike: true,
    emptyCheck: (blocks) => {
      for (const block of blocks) {
        const content = block.finalContent?.type === "text" ? block.finalContent.text : null;
        if (content) {
          try {
            const data: unknown = JSON.parse(content);
            if (
              typeof data === "object" &&
              data !== null &&
              "type" in data &&
              data.type === "scout" &&
              "emails" in data &&
              Array.isArray(data.emails) &&
              data.emails.length > 0
            ) {
              return false;
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
      return true;
    },
    emptyMessageKey: "scout_no_results",
  },
  progress: {
    activities: ["progress"],
    category: "system",
    iconKey: "hourglass",
    titleKey: "entity.progress",
    colorClass: "bg-orange-500 dark:bg-orange-400",
    textColorClass: "text-orange-500 dark:text-orange-400",
    isProgressOperation: true,
  },
  summary: {
    activities: ["summary"],
    category: "answer",
    iconKey: "file-text",
    titleKey: "entity.summary",
    colorClass: "bg-blue-600 dark:bg-blue-500",
    textColorClass: "text-blue-600 dark:text-blue-400",
  },
  system_heartbeat: {
    activities: ["system_heartbeat"],
    category: "system",
    iconKey: "none",
    titleKey: "",
    colorClass: "",
    textColorClass: "",
    isSystemOperation: true,
    isProgressOperation: true,
  },
  system_loading: {
    activities: ["system_loading"],
    category: "system",
    iconKey: "none",
    titleKey: "",
    colorClass: "",
    textColorClass: "",
    isSystemOperation: true,
    isProgressOperation: true,
  },
  system_preparing: {
    activities: ["system_preparing"],
    category: "system",
    iconKey: "none",
    titleKey: "",
    colorClass: "",
    textColorClass: "",
    isSystemOperation: true,
    isProgressOperation: true,
  },
  evaluator: {
    activities: ["evaluator_v1"],
    category: "system",
    iconKey: "none",
    titleKey: "",
    colorClass: "",
    textColorClass: "",
    isSystemOperation: true,
    shouldAutoCollapse: true,
  },
  conversation_complete: {
    activities: ["conversation_complete"],
    category: "system",
    iconKey: "none",
    titleKey: "",
    colorClass: "",
    textColorClass: "",
    isSystemOperation: true,
  },
  task: {
    activities: ["task", "agent_task"],
    category: "task",
    iconKey: "robot",
    titleKey: "activity_agent_task",
    titleKeyComplete: "activity_agent_task_complete",
    colorClass: "bg-indigo-600 dark:bg-indigo-500",
    textColorClass: "text-indigo-600 dark:text-indigo-400",
    categoryLabelKey: "entity.agents",
  },
  skills: {
    activities: ["skills"],
    category: "skills",
    iconKey: "sparkles",
    titleKey: "activity_loading_skill",
    titleKeyComplete: "activity_loaded_skill",
    colorClass: "bg-violet-600 dark:bg-violet-500",
    textColorClass: "text-violet-600 dark:text-violet-400",
    categoryLabelKey: "activity_category_skills",
    shouldAutoCollapse: true,
  },
  crafter: {
    activities: ["crafter"],
    category: "crafter",
    iconKey: "wand",
    titleKey: "activity_crafting_skill",
    titleKeyComplete: "activity_crafted_skill",
    colorClass: "bg-amber-600 dark:bg-amber-500",
    textColorClass: "text-amber-600 dark:text-amber-400",
    categoryLabelKey: "activity_category_skills",
    shouldAutoCollapse: false,
  },
  collaborate: {
    activities: ["collaborate"],
    category: "collaborate",
    iconKey: "message-circle",
    titleKey: "activity_collaborating",
    titleKeyComplete: "activity_collaborated",
    colorClass: "bg-cyan-600 dark:bg-cyan-500",
    textColorClass: "text-cyan-600 dark:text-cyan-400",
    categoryLabelKey: "activity_category_collaboration",
    shouldAutoCollapse: true,
  },
  memory: {
    activities: ["memory"],
    category: "memory",
    iconKey: "database",
    titleKey: "memory.saved",
    colorClass: "bg-emerald-600 dark:bg-emerald-500",
    textColorClass: "text-emerald-600 dark:text-emerald-400",
    shouldAutoCollapse: false,
  },
  task_board: {
    activities: ["task_board"],
    category: "task_board",
    iconKey: "clipboard-list",
    titleKey: "activity_task_board",
    titleKeyComplete: "activity_task_board_complete",
    colorClass: "bg-emerald-600 dark:bg-emerald-500",
    textColorClass: "text-emerald-600 dark:text-emerald-400",
    categoryLabelKey: "activity_category_task_board",
    shouldAutoCollapse: true,
  },

  // Integration tools reach the UI under two different strings. A tool the main
  // agent calls collapses into one operation per provider, keyed by the provider
  // id below ("notion", "slack", …). The same tool called by a sub-agent gets a
  // per-call activity ("agent_tool_<id>") the registry can't know, so the lookup
  // falls back to the raw tool name. Both must be registered or the row renders
  // the snake_case tool name.
  notion: { ...notionTool(["notion"], "activity_working_notion", "activity_category_notion"), isTimelineTool: true },
  notion_search: notionTool(["notion_search"], "activity_searching_notion", "activity_searched_notion"),
  notion_read_page: notionTool(["notion_read_page"], "activity_reading_notion_page", "activity_read_notion_page"),
  notion_create_page: notionTool(
    ["notion_create_page"],
    "activity_creating_notion_page",
    "activity_created_notion_page"
  ),
  notion_update_page: notionTool(
    ["notion_update_page"],
    "activity_updating_notion_page",
    "activity_updated_notion_page"
  ),
  notion_move_page: notionTool(["notion_move_page"], "activity_moving_notion_page", "activity_moved_notion_page"),
  notion_add_comment: notionTool(
    ["notion_add_comment"],
    "activity_adding_notion_comment",
    "activity_added_notion_comment"
  ),
  notion_read_comments: notionTool(
    ["notion_read_comments"],
    "activity_reading_notion_comments",
    "activity_read_notion_comments"
  ),
  notion_list_users: notionTool(["notion_list_users"], "activity_listing_notion_users", "activity_listed_notion_users"),
  notion_create_database: notionTool(
    ["notion_create_database"],
    "activity_creating_notion_database",
    "activity_created_notion_database"
  ),
  notion_update_database: notionTool(
    ["notion_update_database"],
    "activity_updating_notion_database",
    "activity_updated_notion_database"
  ),
  notion_query_database: notionTool(
    ["notion_query_database"],
    "activity_querying_notion_database",
    "activity_queried_notion_database"
  ),
  notion_create_db_row: notionTool(
    ["notion_create_db_row"],
    "activity_creating_notion_db_row",
    "activity_created_notion_db_row"
  ),
  notion_update_db_row: notionTool(
    ["notion_update_db_row"],
    "activity_updating_notion_db_row",
    "activity_updated_notion_db_row"
  ),

  linear: { ...linearTool(["linear"], "activity_working_linear", "activity_category_linear"), isTimelineTool: true },
  linear_search_issues: linearTool(
    ["linear_search_issues"],
    "activity_searching_linear_issues",
    "activity_searched_linear_issues"
  ),
  linear_get_issue: linearTool(["linear_get_issue"], "activity_reading_linear_issue", "activity_read_linear_issue"),
  linear_create_issue: linearTool(
    ["linear_create_issue"],
    "activity_creating_linear_issue",
    "activity_created_linear_issue"
  ),
  linear_update_issue: linearTool(
    ["linear_update_issue"],
    "activity_updating_linear_issue",
    "activity_updated_linear_issue"
  ),
  linear_add_comment: linearTool(
    ["linear_add_comment"],
    "activity_adding_linear_comment",
    "activity_added_linear_comment"
  ),
  linear_read_comments: linearTool(
    ["linear_read_comments"],
    "activity_reading_linear_comments",
    "activity_read_linear_comments"
  ),
  linear_list_teams: linearTool(["linear_list_teams"], "activity_listing_linear_teams", "activity_listed_linear_teams"),
  linear_list_projects: linearTool(
    ["linear_list_projects"],
    "activity_listing_linear_projects",
    "activity_listed_linear_projects"
  ),
  linear_get_project: linearTool(
    ["linear_get_project"],
    "activity_reading_linear_project",
    "activity_read_linear_project"
  ),
  linear_list_initiatives: linearTool(
    ["linear_list_initiatives"],
    "activity_listing_linear_initiatives",
    "activity_listed_linear_initiatives"
  ),
  linear_read_document: linearTool(
    ["linear_read_document"],
    "activity_reading_linear_document",
    "activity_read_linear_document"
  ),
  linear_list_users: linearTool(["linear_list_users"], "activity_listing_linear_users", "activity_listed_linear_users"),

  github: { ...githubTool(["github"], "activity_working_github", "activity_category_github"), isTimelineTool: true },
  github_search_issues: githubTool(
    ["github_search_issues"],
    "activity_searching_github_issues",
    "activity_searched_github_issues"
  ),
  github_get_issue: githubTool(["github_get_issue"], "activity_reading_github_issue", "activity_read_github_issue"),
  github_get_pull_request: githubTool(
    ["github_get_pull_request"],
    "activity_reading_github_pull_request",
    "activity_read_github_pull_request"
  ),
  github_read_comments: githubTool(
    ["github_read_comments"],
    "activity_reading_github_comments",
    "activity_read_github_comments"
  ),
  github_list_repos: githubTool(["github_list_repos"], "activity_listing_github_repos", "activity_listed_github_repos"),
  github_list_users: githubTool(["github_list_users"], "activity_listing_github_users", "activity_listed_github_users"),
  github_list_discussions: githubTool(
    ["github_list_discussions"],
    "activity_listing_github_discussions",
    "activity_listed_github_discussions"
  ),
  github_get_discussion: githubTool(
    ["github_get_discussion"],
    "activity_reading_github_discussion",
    "activity_read_github_discussion"
  ),
  github_ci_status: githubTool(["github_ci_status"], "activity_reading_github_ci", "activity_read_github_ci"),
  github_create_issue: githubTool(
    ["github_create_issue"],
    "activity_creating_github_issue",
    "activity_created_github_issue"
  ),
  github_add_comment: githubTool(
    ["github_add_comment"],
    "activity_adding_github_comment",
    "activity_added_github_comment"
  ),
  github_update_issue: githubTool(
    ["github_update_issue"],
    "activity_updating_github_issue",
    "activity_updated_github_issue"
  ),

  gdrive: { ...gdriveTool(["gdrive"], "activity_working_gdrive", "activity_category_gdrive"), isTimelineTool: true },
  gdrive_search: gdriveTool(["gdrive_search"], "activity_searching_gdrive", "activity_searched_gdrive"),
  gdrive_read_file: gdriveTool(["gdrive_read_file"], "activity_reading_gdrive_file", "activity_read_gdrive_file"),
  gdrive_list_folder: gdriveTool(
    ["gdrive_list_folder"],
    "activity_listing_gdrive_folder",
    "activity_listed_gdrive_folder"
  ),
  gdrive_create_doc: gdriveTool(["gdrive_create_doc"], "activity_creating_gdrive_doc", "activity_created_gdrive_doc"),
  gdrive_update_doc: gdriveTool(["gdrive_update_doc"], "activity_updating_gdrive_doc", "activity_updated_gdrive_doc"),

  slack: { ...slackTool(["slack"], "activity_working_slack", "activity_category_slack"), isTimelineTool: true },
  slack_search_messages: slackTool(["slack_search_messages"], "activity_searching_slack", "activity_searched_slack"),
  slack_read_channel: slackTool(
    ["slack_read_channel"],
    "activity_reading_slack_channel",
    "activity_read_slack_channel"
  ),
  slack_read_thread: slackTool(["slack_read_thread"], "activity_reading_slack_thread", "activity_read_slack_thread"),
  slack_list_channels: slackTool(
    ["slack_list_channels"],
    "activity_listing_slack_channels",
    "activity_listed_slack_channels"
  ),
  slack_lookup_user: slackTool(["slack_lookup_user"], "activity_looking_up_slack_user", "activity_found_slack_user"),
  slack_send_message: slackTool(
    ["slack_send_message"],
    "activity_sending_slack_message",
    "activity_sent_slack_message"
  ),
  slack_send_dm: slackTool(["slack_send_dm"], "activity_sending_slack_dm", "activity_sent_slack_dm"),
  slack_reply_in_thread: slackTool(
    ["slack_reply_in_thread"],
    "activity_replying_slack_thread",
    "activity_replied_slack_thread"
  ),
  slack_add_reaction: slackTool(
    ["slack_add_reaction"],
    "activity_adding_slack_reaction",
    "activity_added_slack_reaction"
  ),

  gmail: { ...gmailTool(["gmail"], "activity_working_gmail", "activity_category_gmail"), isTimelineTool: true },
  gmail_read: gmailTool(["gmail_read"], "activity_reading_email", "activity_read_email"),
  gmail_send: gmailTool(["gmail_send"], "activity_sending_email", "activity_sent_email"),
  gmail_draft: gmailTool(["gmail_draft"], "activity_drafting_email", "activity_drafted_email"),
  gmail_modify: gmailTool(["gmail_modify"], "activity_updating_email", "activity_updated_email"),

  calendar: {
    ...calendarTool(["calendar"], "activity_working_calendar", "activity_category_calendar"),
    isTimelineTool: true,
  },
  // Chats recorded before the provider operation was renamed carry "calendar_read"
  // as the provider activity, so it has to keep resolving.
  calendar_read: calendarTool(["calendar_read"], "activity_reading_calendar", "activity_read_calendar"),
  calendar_list: calendarTool(["calendar_list"], "activity_listing_calendars", "activity_listed_calendars"),
  calendar_write: calendarTool(["calendar_write"], "activity_updating_calendar", "activity_updated_calendar"),
  calendar_freebusy: calendarTool(
    ["calendar_freebusy"],
    "activity_checking_availability",
    "activity_checked_availability"
  ),

  contacts: {
    ...contactsTool(["contacts", "contacts_search"], "activity_searching_contacts", "activity_searched_contacts"),
    isTimelineTool: true,
  },

  // A call is empty only when its results payload says so: a failed call carries
  // no payload and has to stay visible. A plan denial is an answer, not a miss.
  search_chats: {
    activities: ["search_chats", "chat_search"],
    category: "chats",
    iconKey: "message",
    titleKey: "activity_searching_chats",
    titleKeyComplete: "activity_searched_chats",
    colorClass: "bg-brand",
    textColorClass: "text-brand",
    categoryLabelKey: "activity_category_chats",
    shouldAutoCollapse: true,
    canBeEmpty: true,
    emptyCheck: (blocks) =>
      blocks.some((block) => {
        const payload = jsonPayload(block);
        return (
          payload?.toolName === "search_chats" &&
          Array.isArray(payload.hits) &&
          payload.hits.length === 0 &&
          !payload.gateDenied
        );
      }),
    emptyMessageKey: "empty.no_results",
  },
  load_chat: {
    activities: ["load_chat", "chat_load"],
    category: "chats",
    iconKey: "message",
    titleKey: "activity_loading_chat",
    titleKeyComplete: "activity_loaded_chat",
    colorClass: "bg-brand",
    textColorClass: "text-brand",
    categoryLabelKey: "activity_category_chats",
  },
  // Chats recorded before the tool was renamed carry "search_live_sessions" as a
  // sub-agent row's tool name, so it has to keep resolving.
  live_session_search: {
    activities: ["live_session_search", "search_live_sessions"],
    category: "live_sessions",
    iconKey: "wave-square",
    titleKey: "activity_searching_live_sessions",
    titleKeyComplete: "activity_searched_live_sessions",
    colorClass: "bg-brand",
    textColorClass: "text-brand",
    categoryLabelKey: "activity_category_live_sessions",
    shouldAutoCollapse: true,
    canBeEmpty: true,
    emptyCheck: (blocks) =>
      blocks.some((block) => {
        const payload = jsonPayload(block);
        return (
          payload?.type === "live_session_search_results" &&
          Array.isArray(payload.hits) &&
          payload.hits.length === 0 &&
          !payload.userError
        );
      }),
    emptyMessageKey: "empty.no_results",
  },

  // The per-tool operations of an MCP server carry the model tool name,
  // "<slug>__<tool>", and any server can be connected, so they resolve through
  // the double-underscore rule in getActivityConfig rather than a listing here.
  mcp: { ...mcpTool(["mcp"], "activity_working_mcp", "activity_category_mcp"), isTimelineTool: true },

  // The connect card the model shows when a request needs an integration the
  // user doesn't have. Not a timeline tool: it suggests one, it doesn't use it.
  integration_suggestion: {
    activities: ["integration_suggestion"],
    category: "integrations",
    iconKey: "plug",
    titleKey: "activity_suggesting_integration",
    titleKeyComplete: "activity_suggested_integration",
    colorClass: "bg-brand",
    textColorClass: "text-brand",
    categoryLabelKey: "activity_category_integrations",
    shouldAutoCollapse: true,
  },

  // A message the user sent while the turn was still running, delivered at the
  // next tool boundary. It is the user's own words, so it is neither a tool nor
  // auto-collapsed.
  user_interjection: {
    activities: ["user_interjection"],
    category: "user_interjection",
    iconKey: "user",
    titleKey: "activity_user_interjection",
    colorClass: "bg-brand",
    textColorClass: "text-brand",
    categoryLabelKey: "activity_category_user_interjection",
    shouldAutoCollapse: false,
  },

  // The model answering the user mid-turn without ending it. It reads as conversation, not a
  // step, and is kept apart from "answer": a turn's answer is its last one, and a reply counted
  // as an answer would be dropped as a superseded draft.
  reply: {
    activities: ["reply"],
    category: "reply",
    iconKey: "message-circle",
    titleKey: "activity_reply",
    colorClass: "bg-brand",
    textColorClass: "text-brand",
    shouldAutoCollapse: false,
  },
};

const ACTIVITY_LOOKUP_MAP = new Map<string, ActivityConfig>();
for (const config of Object.values(ACTIVITY_REGISTRY)) {
  for (const activity of config.activities) {
    ACTIVITY_LOOKUP_MAP.set(activity, config);
  }
}

export const REGISTERED_ACTIVITIES: readonly string[] = [...ACTIVITY_LOOKUP_MAP.keys()];

export function getActivityConfig(activity: string | undefined): ActivityConfig | undefined {
  if (!activity) return undefined;
  // Builtin tool names never contain a double underscore; MCP slugs never
  // contain a single one, so "__" only ever joins a slug to its tool.
  return ACTIVITY_LOOKUP_MAP.get(activity) ?? (activity.includes("__") ? ACTIVITY_REGISTRY.mcp : undefined);
}

export function getActivityCategory(activity: string | undefined): ActivityCategory {
  return getActivityConfig(activity)?.category || "unknown";
}

export function isSystemOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.isSystemOperation || false;
}

export function isProgressOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.isProgressOperation || false;
}

export function isAnswerOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.category === "answer";
}

export function isReasoningOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.category === "reasoning";
}

function isSearchOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.isSearchLike || false;
}

export function isContentGeneration(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.isContentGeneration || false;
}

export function shouldAutoCollapse(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.shouldAutoCollapse || false;
}

export function getActivityTitle(activity: string | undefined, isComplete: boolean): string | undefined {
  const config = getActivityConfig(activity);
  if (!config) return undefined;
  return isComplete && config.titleKeyComplete ? config.titleKeyComplete : config.titleKey;
}

export function getActivityColorClass(activity: string | undefined): string {
  return getActivityConfig(activity)?.colorClass || "bg-dark-icon";
}

export function getActivityIconKey(activity: string | undefined): string {
  return getActivityConfig(activity)?.iconKey || "check";
}

/**
 * The icon key a category shows.
 *
 * A category is not an activity string and must not be passed to
 * `getActivityIconKey`: several categories are named after the thing produced
 * rather than after any activity that produces it — `image` is the category of
 * `image_generation`, `image-gen` and the retired image tools, and matches none of
 * them — so the activity lookup misses and every caller falls back to the generic
 * check mark.
 */
export function getCategoryIconKey(category: ActivityCategory): string {
  return getActivityConfigByCategory(category)?.iconKey || "check";
}

export function checkToolHasNoResults(operation: StreamingOperation, blocks: StreamingBlock[]): boolean {
  if (!operation.isComplete) return false;
  const config = getActivityConfig(operation.activity) ?? getActivityConfig(operation.name);
  if (!config?.canBeEmpty || !config.emptyCheck) return false;
  return config.emptyCheck(blocks);
}

export function getToolEmptyMessageKey(activity: string | undefined): string | undefined {
  return getActivityConfig(activity)?.emptyMessageKey;
}

export function isAskUserOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.category === "ask_user";
}

export function isMemoryOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.category === "memory";
}

export function isReplyOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.category === "reply";
}

export function isUserInterjectionOperation(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.category === "user_interjection";
}

export function isConversationComplete(activity: string | undefined): boolean {
  return activity === "conversation_complete";
}

export function isSearchActivity(activity: string | undefined): boolean {
  return isSearchOperation(activity);
}

export function isTimelineTool(activity: string | undefined): boolean {
  return getActivityConfig(activity)?.isTimelineTool || false;
}

export function getActivityTextColorClass(activity: string | undefined): string {
  return getActivityConfig(activity)?.textColorClass || "text-gray-600 dark:text-gray-400";
}

const CATEGORY_LOOKUP_MAP = new Map<ActivityCategory, ActivityConfig>();
for (const config of Object.values(ACTIVITY_REGISTRY)) {
  if (!CATEGORY_LOOKUP_MAP.has(config.category)) {
    CATEGORY_LOOKUP_MAP.set(config.category, config);
  }
}

export function getActivityConfigByCategory(category: ActivityCategory): ActivityConfig | undefined {
  return CATEGORY_LOOKUP_MAP.get(category);
}
